import React from "react";
import { getShellStateSnapshot, subscribeToShellState } from "@/main/navigation/ShellStateStore";

/**
 * The support account's username, server-seeded into the shell state, or `null` when this environment has
 * no support account configured.
 *
 * <p>The single client-side source for "is this participant the support account?" (decisions D14/D31).
 * Pair it with `displayNameForParticipant` from the chat module rather than comparing usernames inline:
 * four independent comparisons scattered across the chat list, the thread header, the drawer and the
 * profile page will drift, and the one that drifts is the one on the profile page, where the alias reads
 * as a bug the moment a member clicks the name.
 *
 * <p><b>Never hardcode "TheCustodian" as a fallback.</b> `seavien.support.account-username` exists so no
 * literal names that account anywhere in code — a rename, or a later split of support from moderation, is
 * meant to be a config change. A `null` here means "alias nothing", which is the right degradation:
 * showing a raw username is cosmetic, aliasing the wrong account is not.
 *
 * <p>Same store and subscription shape the removed `useCanAccessModeration` hook used. Reads a field that
 * `EnhancedNavigation` explicitly carries forward across in-app navigations.
 *
 * <p>⚠️ <b>The selector is the subscription, and it is module-level on purpose.</b> This hook used to
 * pass `getShellStateSnapshot` straight to `useSyncExternalStore` and pick the field out of the result,
 * which subscribes the caller to <i>every</i> shell-state change — an unread count, a search term, a
 * profile image — for a value that changes about once per session. `useSyncExternalStore` bails out on
 * `Object.is` of what `getSnapshot` RETURNS, so returning the scalar makes the bail-out do the work.
 * It matters here because `ThreadListItem` calls this, so the cost was paid once per chat thread ROW
 * per shell write (2026-08-29 performance audit, W5 R3). The function must stay a stable module-level
 * constant returning a primitive: a fresh closure re-subscribes on every render, and a fresh object
 * would loop forever. Guard: `shellStateFieldSubscriptions.spec.tsx`.
 */
const readSupportUsername = (): string | null => getShellStateSnapshot().mainHeader?.supportUsername ?? null;

export function useSupportUsername(): string | null {
    return React.useSyncExternalStore(subscribeToShellState, readSupportUsername, readSupportUsername);
}
